import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  CheckCircle2, 
  ArrowRight,
  ShieldCheck,
  Bike,
  Zap
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

const Partnership = () => {
  const benefits = [
    {
      title: "Scale on Demand",
      description: "Instantly increase your delivery capacity during peak hours without hiring internal staff.",
      icon: <BarChart3 size={20} />
    },
    {
      title: "Vetted Workforce",
      description: "Our 55+ bikers are professional, background-checked, and experts in Budapest navigation.",
      icon: <Users size={20} />
    },
    {
      title: "Zero Fleet Overhead",
      description: "Stop worrying about bike maintenance or insurance. We handle the physical infrastructure.",
      icon: <ShieldCheck size={20} />
    }
  ];

  return (
    <main className="min-h-screen bg-white text-[#032B53] selection:bg-[#0099FF] selection:text-white">
      
      {/* --- Section 1: Hero --- */}
      <section className="relative pt-32 pb-24 bg-gray-50/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="h-[1px] w-10 bg-[#0099FF]"></span>
              <span className="text-[#0099FF] font-bold tracking-[0.3em] text-[10px] uppercase">Fleet Outsourcing Budapest</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-10">
              Grow Your <br />
              <span className="text-[#0099FF]">Fleet Capacity.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-500 font-medium leading-relaxed max-w-2xl">
              We provide the physical delivery power for Budapest's leading platforms. 
              Access a vetted workforce of 55+ professional bikers.
            </p>
          </motion.div>
        </div>
        {/* Subtle background element */}
        <div className="absolute top-0 right-0 p-20 opacity-[0.03] pointer-events-none">
          <Bike size={400} />
        </div>
      </section>

      {/* --- Section 2: Why Partner With Us --- */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-16">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index} 
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="space-y-6 group"
              >
                <div className="w-12 h-12 bg-[#0099FF]/10 rounded-2xl flex items-center justify-center text-[#0099FF] transition-colors group-hover:bg-[#0099FF] group-hover:text-white">
                  {benefit.icon}
                </div>
                <h3 className="text-3xl font-black tracking-tighter">{benefit.title}</h3>
                <p className="text-lg text-gray-400 font-medium leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Section 3: The Process --- */}
      <section className="py-24 bg-[#032B53] text-white rounded-[4rem] mx-4 overflow-hidden relative shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter">Onboarding Process</h2>
            <div className="h-1 w-20 bg-[#0099FF] mx-auto mt-6"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-16 relative">
            {[
              { step: "01", title: "Inquiry", desc: "Contact us with your estimated monthly volume and required Budapest districts." },
              { step: "02", title: "Matching", desc: "We assign a dedicated portion of our 55+ bikers to your specific delivery needs." },
              { step: "03", title: "Launch", desc: "Physical delivery operations begin, supporting your shop or restaurant daily." }
            ].map((item, i) => (
              <motion.div 
                key={i}
                {...fadeInUp}
                transition={{ delay: i * 0.2 }}
                className="space-y-6 relative"
              >
                <div className="text-7xl font-black text-white/5 absolute -top-10 -left-4">
                  {item.step}
                </div>
                <div className="text-[#0099FF] font-black text-xl tracking-widest uppercase">Step {item.step}</div>
                <h4 className="text-3xl font-bold tracking-tight">{item.title}</h4>
                <p className="text-blue-100/50 font-medium text-lg leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Section 4: Current Success --- */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            <motion.div {...fadeInUp} className="lg:w-1/2">
              <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-12 leading-[0.9]">
                Trusted by <br /> 
                <span className="text-gray-300">Market Leaders.</span>
              </h2>
              <div className="space-y-4">
                {["Express fork kft", "Wolt", "GM capital 2020"].map((p, i) => (
                  <div key={i} className="flex items-center gap-4 p-8 bg-gray-50 rounded-[2rem] border border-gray-100 hover:border-[#0099FF] transition-colors group">
                    <CheckCircle2 size={20} className="text-[#0099FF]" />
                    <span className="text-2xl font-black tracking-tighter group-hover:text-[#0099FF] transition-colors">{p}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              {...fadeInUp}
              transition={{ delay: 0.3 }}
              className="lg:w-1/2 bg-[#032B53] p-16 rounded-[3.5rem] text-white relative overflow-hidden"
            >
              <Zap size={150} className="absolute -right-10 -top-10 text-white/5" />
              <div className="space-y-10 relative z-10">
                <div>
                  <h4 className="text-[#0099FF] font-bold uppercase tracking-[0.3em] text-[10px] mb-4">Operations Milestone</h4>
                  <p className="text-7xl font-black tracking-tighter mb-4">20,000+</p>
                  <p className="text-blue-100/60 text-lg font-medium">Successfully completed deliveries through our partner networks across Budapest.</p>
                </div>
                <div className="pt-10 border-t border-white/10">
                  <Link 
                    to="/contact" 
                    className="flex items-center justify-between group"
                  >
                    <span className="text-2xl font-black tracking-tighter group-hover:text-[#0099FF] transition-colors">Apply for Fleet Access</span>
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-[#0099FF] transition-all">
                      <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- Section 5: Final Call --- */}
      <section className="py-40 text-center">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto px-6"
        >
          <h2 className="text-6xl md:text-9xl font-black tracking-tighter mb-16">Let's work.</h2>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-6 bg-[#032B53] text-white px-12 py-6 rounded-3xl text-xl font-black hover:bg-[#0099FF] hover:shadow-2xl hover:shadow-blue-500/20 transition-all active:scale-95"
          >
            Start Partnership
            <ArrowRight size={24} />
          </Link>
          <div className="mt-20 flex justify-center items-center gap-2 opacity-20">
            <Bike size={14} />
            <span className="text-[10px] font-bold uppercase tracking-[0.5em]">Budapest Logistics Partner</span>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Partnership;